import { useState, useEffect } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { API } from "@/apiConfig";
import { toTitleCase } from "@/utils/titleCase";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const EMPTY_FORM = {
  company_name: "", vendor_type: "purchases", expense_category: "", expense_subcategory: "",
  sales_rep: "", phone: "", mobile: "",
  order_day: "", order_day_2: "", delivery_day: "", delivery_day_2: "", notes: ""
};

export default function VendorDialog({ open, onOpenChange, onVendorAdded }) {
  const [vendorForm, setVendorForm] = useState({ ...EMPTY_FORM });
  const [expCategories, setExpCategories] = useState([]);
  const [expSubcategories, setExpSubcategories] = useState([]);
  const [cogSubcategories, setCogSubcategories] = useState([]);
  const [addingCat, setAddingCat] = useState(false);
  const [addingSub, setAddingSub] = useState(false);
  const [addingCogSub, setAddingCogSub] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newSubName, setNewSubName] = useState("");
  const [newCogSubName, setNewCogSubName] = useState("");
  const [cogCategoryId, setCogCategoryId] = useState("");
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };


  const ensureCogCategory = useStableCallback(async () => {
    try {
      const r = await axios.post(`${API}/accounting/expense-categories`, { name: "Cost Of Goods" }, { headers });
      setCogCategoryId(r.data.id);
      fetchCogSubcategories(r.data.id);
    } catch {}
  });

  const fetchCogSubcategories = async (catId) => {
    try { const r = await axios.get(`${API}/accounting/expense-subcategories?category_id=${catId}`, { headers }); setCogSubcategories(r.data); } catch {}
  };

  const handleAddCogSubcategory = async () => {
    if (!newCogSubName.trim() || !cogCategoryId) return;
    try {
      const r = await axios.post(`${API}/accounting/expense-subcategories`, { name: newCogSubName.trim(), category_id: cogCategoryId }, { headers });
      await fetchCogSubcategories(cogCategoryId);
      setVendorForm(f => ({ ...f, expense_subcategory: r.data.id }));
      setNewCogSubName("");
      setAddingCogSub(false);
    } catch { toast.error("Failed to add subcategory"); }
  };

  const fetchExpCategories = useStableCallback(async () => {
    try { const r = await axios.get(`${API}/accounting/expense-categories`, { headers }); setExpCategories(r.data); } catch {}
  });

  const fetchExpSubcategories = useStableCallback(async (catId) => {
    try { const r = await axios.get(`${API}/accounting/expense-subcategories?category_id=${catId}`, { headers }); setExpSubcategories(r.data); } catch {}
  });

  useEffect(() => {
    if (open) {
      fetchExpCategories();
      ensureCogCategory();
      setVendorForm({ ...EMPTY_FORM });
      setAddingCat(false);
      setAddingSub(false);
      setAddingCogSub(false);
    }
  }, [open, fetchExpCategories, ensureCogCategory]);

  useEffect(() => {
    if (vendorForm.expense_category) fetchExpSubcategories(vendorForm.expense_category);
    else setExpSubcategories([]);
  }, [vendorForm.expense_category, fetchExpSubcategories]);

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const r = await axios.post(`${API}/accounting/expense-categories`, { name: newCatName.trim() }, { headers });
      await fetchExpCategories();
      setVendorForm(f => ({ ...f, expense_category: r.data.id }));
      setNewCatName("");
      setAddingCat(false);
    } catch { toast.error("Failed to add category"); }
  };

  const handleAddSubcategory = async () => {
    if (!newSubName.trim() || !vendorForm.expense_category) return;
    try {
      const r = await axios.post(`${API}/accounting/expense-subcategories`, { name: newSubName.trim(), category_id: vendorForm.expense_category }, { headers });
      await fetchExpSubcategories(vendorForm.expense_category);
      setVendorForm(f => ({ ...f, expense_subcategory: r.data.id }));
      setNewSubName("");
      setAddingSub(false);
    } catch { toast.error("Failed to add subcategory"); }
  };

  const handleAddVendor = async () => {
    if (!vendorForm.company_name.trim()) { toast.error("Company name is required"); return; }
    try {
      const response = await axios.post(`${API}/vendors`, vendorForm, { headers });
      setVendorForm({ ...EMPTY_FORM });
      onOpenChange(false);
      onVendorAdded(response.data);
      toast.success("Vendor added");
    } catch (error) {
      if (error.response?.status === 503) toast.error("Can't reach Central — check your internet connection and try again.");
      else toast.error(error.response?.data?.detail || "Failed to add vendor");
    }
  };

  const isExpense = vendorForm.vendor_type === "expenses";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Vendor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="vendor_company">Company Name *</Label>
            <Input id="vendor_company" value={vendorForm.company_name}
              onChange={(e) => setVendorForm({ ...vendorForm, company_name: e.target.value.toUpperCase() })} required
              data-testid="vendor-dialog-company-input" />
          </div>

          {/* Type: Purchases or Expenses */}
          <div>
            <Label>Type *</Label>
            <div className="flex gap-2 mt-1">
              {["purchases", "expenses"].map(t => (
                <button key={t} onClick={() => {
                    const updates = { ...vendorForm, vendor_type: t, expense_category: "", expense_subcategory: "" };
                    if (t === "purchases" && cogCategoryId) updates.expense_category = cogCategoryId;
                    setVendorForm(updates);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    vendorForm.vendor_type === t ? "bg-blue-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200"
                  }`} data-testid={`vendor-type-${t}`}
                >
                  {t === "purchases" ? "Purchases" : "Expenses"}
                </button>
              ))}
            </div>
          </div>

          {/* Purchases: Cost Of Goods category + Subcategory */}
          {vendorForm.vendor_type === "purchases" && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-zinc-500">Category:</Label>
                <span className="text-sm font-semibold text-zinc-800">Cost Of Goods</span>
              </div>
              <Label>Subcategory</Label>
              {addingCogSub ? (
                <div className="flex gap-1 mt-1">
                  <Input className="flex-1" placeholder="New subcategory name..." value={newCogSubName}
                    onChange={(e) => setNewCogSubName(toTitleCase(e.target.value))}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddCogSubcategory(); }}
                  />
                  <Button size="sm" onClick={handleAddCogSubcategory} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => setAddingCogSub(false)}>Cancel</Button>
                </div>
              ) : (
                <Select value={vendorForm.expense_subcategory} onValueChange={(v) => {
                  if (v === "__add__") { setAddingCogSub(true); return; }
                  setVendorForm({ ...vendorForm, expense_category: cogCategoryId, expense_subcategory: v });
                }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select subcategory..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__add__" className="text-blue-600 font-medium">+ Add New Subcategory</SelectItem>
                    {cogSubcategories.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Expense Category + Subcategory (only for Expenses) */}
          {isExpense && (
            <>
              <div>
                <Label>Expense Category</Label>
                {addingCat ? (
                  <div className="flex gap-1 mt-1">
                    <Input className="flex-1" placeholder="New category name..." value={newCatName}
                      onChange={(e) => setNewCatName(toTitleCase(e.target.value))}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
                    />
                    <Button size="sm" onClick={handleAddCategory} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingCat(false)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex gap-1 mt-1">
                    <Select value={vendorForm.expense_category} onValueChange={(v) => {
                      if (v === "__add__") { setAddingCat(true); return; }
                      setVendorForm({ ...vendorForm, expense_category: v, expense_subcategory: "" });
                    }}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select category..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__add__" className="text-blue-600 font-medium">+ Add New Category</SelectItem>
                        {expCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {vendorForm.expense_category && (
                <div>
                  <Label>Subcategory</Label>
                  {addingSub ? (
                    <div className="flex gap-1 mt-1">
                      <Input className="flex-1" placeholder="New subcategory name..." value={newSubName}
                        onChange={(e) => setNewSubName(toTitleCase(e.target.value))}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddSubcategory(); }}
                      />
                      <Button size="sm" onClick={handleAddSubcategory} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setAddingSub(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex gap-1 mt-1">
                      <Select value={vendorForm.expense_subcategory} onValueChange={(v) => {
                        if (v === "__add__") { setAddingSub(true); return; }
                        setVendorForm({ ...vendorForm, expense_subcategory: v });
                      }}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Select subcategory..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__add__" className="text-blue-600 font-medium">+ Add New Subcategory</SelectItem>
                          {expSubcategories.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div>
            <Label htmlFor="vendor_rep">Sales Rep</Label>
            <Input id="vendor_rep" value={vendorForm.sales_rep}
              onChange={(e) => setVendorForm({ ...vendorForm, sales_rep: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vendor_phone">Phone</Label>
              <Input id="vendor_phone" value={vendorForm.phone}
                onChange={(e) => setVendorForm({ ...vendorForm, phone: formatPhoneNumber(e.target.value) })} placeholder="(000) 000-0000" />
            </div>
            <div>
              <Label htmlFor="vendor_mobile">Mobile</Label>
              <Input id="vendor_mobile" value={vendorForm.mobile}
                onChange={(e) => setVendorForm({ ...vendorForm, mobile: formatPhoneNumber(e.target.value) })} placeholder="(000) 000-0000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="order_day">Order Day 1</Label>
              <select id="order_day" value={vendorForm.order_day}
                onChange={(e) => setVendorForm({ ...vendorForm, order_day: e.target.value })}
                className="w-full p-2 border border-zinc-200 rounded-md">
                <option value="">Select Day</option>
                {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="order_day_2">Order Day 2</Label>
              <select id="order_day_2" value={vendorForm.order_day_2}
                onChange={(e) => setVendorForm({ ...vendorForm, order_day_2: e.target.value })}
                className="w-full p-2 border border-zinc-200 rounded-md">
                <option value="">Select Day</option>
                {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="delivery_day">Delivery Day 1</Label>
              <select id="delivery_day" value={vendorForm.delivery_day}
                onChange={(e) => setVendorForm({ ...vendorForm, delivery_day: e.target.value })}
                className="w-full p-2 border border-zinc-200 rounded-md">
                <option value="">Select Day</option>
                {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="delivery_day_2">Delivery Day 2</Label>
              <select id="delivery_day_2" value={vendorForm.delivery_day_2}
                onChange={(e) => setVendorForm({ ...vendorForm, delivery_day_2: e.target.value })}
                className="w-full p-2 border border-zinc-200 rounded-md">
                <option value="">Select Day</option>
                {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="vendor_notes">Notes</Label>
            <textarea id="vendor_notes" value={vendorForm.notes}
              onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })}
              className="w-full p-2 border border-zinc-200 rounded-md h-20" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddVendor} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="vendor-dialog-add-btn">
              Add Vendor
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
